import React from 'react';
import instance from '../instance';
import $config from '../config';
import {GridToolbarExport, useGridSlotComponentProps} from '@material-ui/data-grid';
import {Pagination} from '@material-ui/lab';

/**
 * Запрос к API "/LK".
 * @param request Параметры запроса.
 * @param handleErrorNotification
 * @param showSuccess
 * @return  AxiosResponse<any>|undefined
 */
const ApiRequest = async (request: any, handleErrorNotification: any = undefined, showSuccess: boolean = false) => {
    try {
        const response = await instance.post('/LK', request);
        if (handleErrorNotification) {
            handleErrorNotification(response, showSuccess);
        }
        return response;
    } catch (err) {
        if ($config.debugMode & $config.D_ERR) {
            console.log(request.method + ': Ошибка: ' + err.message);
        }
    }
    return undefined;
};

/**
 * Панель постраничного просмотра.
 * @constructor
 */
const NavCustomPagination = (prop: any) => {
    const {state, apiRef} = useGridSlotComponentProps();
    return (
        <Pagination
            className="pagination"
            variant={'outlined'}
            color={'primary'}
            // showLastButton={true}
            // showFirstButton={true}
            count={state.pagination.pageCount}
            page={state.pagination.page + 1}
            onChange={(event, value) => apiRef.current.setPage(value - 1)}
        />
    );
};

const CustomGridToolbarExport = (prop: any) => {
    return (
        <GridToolbarExport
            {...prop}
            startIcon={
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16 9L12 5L8 9" stroke="#33415B" strokeWidth="2" strokeLinecap="round" />
                    <path d="M12 5V13" stroke="#33415B" strokeWidth="2" strokeLinecap="round" />
                    <path
                        d="M4 14V17.5C4 18.0523 4.44772 18.5 5 18.5H19C19.5523 18.5 20 18.0523 20 17.5V14"
                        stroke="#33415B"
                        strokeWidth="2"
                        strokeLinecap="round"
                    />
                </svg>
            }
        />
    );
};

const sleep = (s: number) => new Promise(resolve => setTimeout(resolve, s * 1000));

/**
 * Структура для параметров метода {@link SaveBlobToFile SaveBlobToFile()}.
 */
export interface ISaveBlob {
    data: Blob;
    filename: string;
}

/**
 * Сохранить BLOB-данные в файл.
 * @param prop - Параметры типа {@link ISaveBlob}.
 * @constructor
 */
const SaveBlobToFile = (prop: ISaveBlob) => {
    const url = URL.createObjectURL(prop.data);
    const a = document.createElement('a');
    a.href = url;
    a.hidden = true;
    a.download = prop.filename;
    a.click();
    setTimeout(() => {
        URL.revokeObjectURL(url);
    });
};

/**
 * Структура для параметров метода {@link SaveCSVToFile SaveCSVToFile()}.
 */
export interface ISaveCSV {
    data: string;
    filename: string;
}

/**
 * Сохранить CSV-данные в CSV-файл.
 * @param prop - Параметры типа {@link ISaveCSV}.
 * @constructor
 */
const SaveCSVToFile = (prop: ISaveCSV) => {
    const sbProp: ISaveBlob = {
        data: new Blob([prop.data], {
            type: 'text/csv',
        }),
        filename: prop.filename,
    };
    SaveBlobToFile(sbProp);
};

/**
 * Структура для параметров метода {@link SaveCSVToFile SaveCSVToFile()}.
 */
export interface ISaveBase64CSV {
    base64: string;
    filename: string;
    ext: string;
    postfix?: any;
    encoding?: 'utf-8' | 'latin1';
}

/**
 * Сохранить CSV-данные в CSV-файл из base64-строки (V2).
 * @param prop - Параметры типа {@link ISaveBase64CSV}.
 * @constructor
 */
const SaveBase64CSVToFile2 = (prop: ISaveBase64CSV) => {
    if (prop.base64.length <= 0) {
        return;
    }
    let a, url: string;
    if (typeof prop.postfix == 'boolean' && prop.postfix) {
        prop.postfix = new Date().toISOString().slice(0, 19).replaceAll(':', '-').replace('T', ' ');
    }
    url = 'data:text/csv;base64,' + prop.base64;
    a = document.createElement('a');
    a.hidden = true;
    a.href = url;
    a.download = prop.filename;
    if (prop.postfix && prop.postfix.length > 0) {
        a.download += ', ' + prop.postfix;
    }
    if (prop.ext.length > 0) {
        a.download += '.' + prop.ext;
    }
    a.click();
    setTimeout(() => {
        URL.revokeObjectURL(url);
    });
};

/**
 * Сохранить CSV-данные в CSV-файл из base64-строки.
 * @param prop - Параметры типа {@link ISaveBase64CSV}.
 * @constructor
 */
const SaveBase64CSVToFile = (prop: ISaveBase64CSV) => {
    if (prop.base64.length <= 0) {
        return;
    }

    let blobData: Blob;

    if (prop.encoding === 'utf-8') {
        // Для UTF-8: декодируем base64 и создаем Blob с указанием кодировки
        const binaryString = atob(prop.base64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        blobData = new Blob([bytes], { type: 'text/csv;charset=utf-8' });
    } else {
        // Для Latin1 (старый вариант) или по умолчанию
        blobData = new Blob([Uint8Array.from(atob(prop.base64), x => x.charCodeAt(0))], {
            type: 'text/csv',
        });
    }

    const sbProp: ISaveBlob = {
        data: blobData,
        filename: prop.filename,
    };

    if ((typeof prop.postfix == 'boolean' && prop.postfix) || prop.filename.length <= 0) {
        prop.postfix = new Date().toISOString().slice(0, 19).replaceAll(':', '-').replace('T', ' ');
    }

    if (prop.postfix && prop.postfix.length > 0) {
        if (sbProp.filename.length > 0) {
            sbProp.filename += ', ';
        }

        sbProp.filename += prop.postfix;
    }

    if (prop.ext.length > 0) {
        sbProp.filename += '.' + prop.ext;
    }

    SaveBlobToFile(sbProp);
};

export default ApiRequest;
export {NavCustomPagination, CustomGridToolbarExport, sleep, SaveBlobToFile, SaveCSVToFile, SaveBase64CSVToFile};
